import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ReliabilityBar } from '../components/ReliabilityBar'
import { TeamPanel } from '../components/TeamPanel'
import { VolatilityChip } from '../components/VolatilityChip'
import { useAuth } from '../lib/auth'
import { checkoutHref } from '../lib/checkout'
import { apiFetch } from '../lib/supabase'

interface Account {
  email: string
  has_schedules: boolean
  plan: string
  subscription_status: string | null
}
interface ApiKey {
  id: string
  name: string
  key_prefix: string
  last_used_at: string | null
  created_at: string
}

export function DashboardPage() {
  const { session, loading, signOut } = useAuth()
  const nav = useNavigate()
  const [account, setAccount] = useState<Account | null>(null)
  const [keys, setKeys] = useState<ApiKey[]>([])
  const [newKey, setNewKey] = useState<string | null>(null)
  const [loadingData, setLoadingData] = useState(true)

  useEffect(() => {
    if (!loading && !session) nav('/login')
  }, [loading, session, nav])

  useEffect(() => {
    if (!session) return
    ;(async () => {
      const [a, k] = await Promise.all([apiFetch('/account'), apiFetch('/api-keys')])
      if (a.ok) setAccount(a.json.data)
      if (k.ok) setKeys(k.json.data ?? [])
      setLoadingData(false)
    })()
  }, [session])

  // New keys are issued by our team (they set the environment + caps). Clients re-roll what they hold:
  // rotate mints a fresh secret that inherits the old key's tier + caps, shown once.
  const [rotatingId, setRotatingId] = useState<string | null>(null)
  async function rotateKey(id: string) {
    if (
      !confirm(
        'Rotate this key? A new key is issued and shown once so you can copy it. The old key stops working immediately.'
      )
    )
      return
    setRotatingId(id)
    try {
      const r = await apiFetch(`/api-keys/${id}/rotate`, { method: 'POST' })
      if (r.ok) {
        setNewKey(r.json.data.key)
        const k = await apiFetch('/api-keys')
        if (k.ok) setKeys(k.json.data ?? [])
      }
    } finally {
      setRotatingId(null)
    }
  }
  async function openPortal() {
    const r = await apiFetch('/billing/portal', { method: 'POST' })
    if (r.ok && r.json.data?.portal_url) window.location.href = r.json.data.portal_url
  }

  if (loading || loadingData) {
    return <main className="max-w-4xl mx-auto px-6 pt-20 text-[var(--text-muted)]">Loading…</main>
  }

  return (
    <main className="max-w-4xl mx-auto px-6 pt-12 pb-16">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1
            className="font-display text-3xl"
            style={{ fontWeight: 210, letterSpacing: '-0.03em' }}
          >
            Dashboard
          </h1>
          <p className="text-sm text-[var(--text-muted)] mt-1">{account?.email}</p>
        </div>
        <button
          type="button"
          onClick={() => signOut().then(() => nav('/'))}
          className="text-sm text-[var(--text-secondary)] hover:text-signal"
        >
          Sign out
        </button>
      </div>

      {/* Plan */}
      <section className="bg-surface-0 border border-border rounded-2xl p-6 mb-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs font-mono uppercase tracking-wide text-[var(--text-muted)]">
              Plan
            </div>
            <div className="font-display font-bold text-xl capitalize mt-1">
              {account?.plan ?? 'free'}
              {account?.has_schedules ? (
                <span className="ml-2 text-[10px] font-mono bg-signal/15 text-signal rounded px-1.5 py-0.5 align-middle">
                  ACTIVE
                </span>
              ) : (
                <span className="ml-2 text-[10px] font-mono bg-warn/15 text-warn rounded px-1.5 py-0.5 align-middle">
                  FREE
                </span>
              )}
            </div>
          </div>
          {account?.has_schedules ? (
            <button
              type="button"
              onClick={openPortal}
              className="border border-border text-[var(--text-primary)] px-4 py-2 rounded-lg text-sm hover:border-signal/50"
            >
              Manage billing
            </button>
          ) : (
            <a
              href={checkoutHref('starter')}
              className="bg-signal text-on-accent font-bold px-4 py-2 rounded-lg text-sm no-underline"
            >
              Upgrade to Starter
            </a>
          )}
        </div>
      </section>

      {/* Team / seats */}
      <TeamPanel />

      {/* API keys */}
      <section className="bg-surface-0 border border-border rounded-2xl p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display font-bold text-lg">API & MCP keys</h2>
          {account?.has_schedules && (
            <a
              href="mailto:sales@navo24.com?subject=API%20key%20request%20(SchedulesMCP)"
              className="border border-border text-[var(--text-primary)] px-3 py-1.5 rounded-lg text-sm no-underline hover:border-signal/50"
            >
              Request a key
            </a>
          )}
        </div>
        {newKey && (
          <div className="bg-surface-1 border border-signal/40 rounded-lg p-3 mb-4">
            <p className="text-xs text-warn mb-1">Copy now, shown once:</p>
            <code className="font-mono text-signal break-all text-sm">{newKey}</code>
          </div>
        )}
        {keys.length === 0 ? (
          <p className="text-sm text-[var(--text-muted)]">
            {account?.has_schedules
              ? 'No keys yet. Contact us to get one.'
              : 'Upgrade to get an API key.'}
          </p>
        ) : (
          <div className="space-y-2">
            {keys.map((k) => (
              <div
                key={k.id}
                className="flex items-center justify-between gap-3 text-sm border-b border-border/50 pb-2 last:border-0"
              >
                <span className="font-mono text-[var(--text-secondary)]">{k.key_prefix}…</span>
                <div className="flex items-center gap-3">
                  <span className="text-[var(--text-muted)] text-xs">{k.name}</span>
                  <button
                    type="button"
                    onClick={() => rotateKey(k.id)}
                    disabled={rotatingId === k.id}
                    className="text-xs text-[var(--text-secondary)] hover:text-signal disabled:opacity-50"
                  >
                    {rotatingId === k.id ? 'Rotating…' : 'Rotate'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        <p className="text-xs text-[var(--text-muted)] mt-4">
          Use a key with the{' '}
          <a href="/mcp" className="text-signal no-underline hover:underline">
            MCP server
          </a>{' '}
          endpoint <code>mcp.schedulesmcp.com/mcp</code> (for your MCP client, not a browser).
        </p>
      </section>

      {account?.has_schedules ? (
        <>
          <AlertInbox />
          <div className="h-6" />
          <LaneLookup />
          <div className="h-6" />
          <LeagueTable />
          <div className="h-6" />
          <WatchLanes />
        </>
      ) : (
        <section className="bg-surface-0 border border-border rounded-2xl p-6 text-center">
          <p className="text-[var(--text-secondary)] mb-4">
            Unlock full carrier reliability across every lane.
          </p>
          <a
            href={checkoutHref('starter')}
            className="bg-signal text-on-accent font-bold px-5 py-2.5 rounded-lg no-underline"
          >
            Upgrade for €49/mo
          </a>
        </section>
      )}
    </main>
  )
}

function LaneLookup() {
  const [origin, setOrigin] = useState('CNSHA')
  const [destination, setDestination] = useState('NLRTM')
  // biome-ignore lint/suspicious/noExplicitAny: API row shape
  const [rows, setRows] = useState<any[] | null>(null)
  const [note, setNote] = useState<string | null>(null)

  async function run() {
    const r = await apiFetch(`/reliability/lanes?origin=${origin}&destination=${destination}`)
    if (r.ok) {
      const carriers = r.json.data?.carriers ?? []
      setRows(carriers)
      setNote(
        carriers.length === 0
          ? 'No displayable reliability for this lane yet. Coverage grows as more shipments are tracked.'
          : null
      )
    }
  }

  return (
    <section className="bg-surface-0 border border-border rounded-2xl p-6">
      <h2 className="font-display font-bold text-lg mb-4">Lane reliability (live)</h2>
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <input
          value={origin}
          onChange={(e) => setOrigin(e.target.value.toUpperCase())}
          className="bg-surface-1 border border-border rounded-lg px-3 py-2 font-mono w-28 text-[var(--text-primary)] focus:border-signal outline-none"
        />
        <span className="pb-2 text-[var(--text-muted)]">→</span>
        <input
          value={destination}
          onChange={(e) => setDestination(e.target.value.toUpperCase())}
          className="bg-surface-1 border border-border rounded-lg px-3 py-2 font-mono w-28 text-[var(--text-primary)] focus:border-signal outline-none"
        />
        <button
          type="button"
          onClick={run}
          className="bg-signal text-on-accent font-bold px-4 py-2 rounded-lg"
        >
          Look up
        </button>
      </div>
      {note && <p className="text-sm text-[var(--text-muted)]">{note}</p>}
      {rows && rows.length > 0 && (
        <div className="space-y-3">
          {rows.map((c) => (
            <div key={c.carrier_code} className="flex items-center justify-between gap-4">
              <span className="font-mono text-sm w-20">{c.carrier_code}</span>
              <ReliabilityBar pct={Math.round(c.on_time_pct ?? 0)} />
              <span className="text-xs text-[var(--text-muted)] w-24 text-right">
                {c.observations} obs
              </span>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}

function LeagueTable() {
  // biome-ignore lint/suspicious/noExplicitAny: API row shape
  const [ranked, setRanked] = useState<any[]>([])
  // biome-ignore lint/suspicious/noExplicitAny: API row shape
  const [provisional, setProvisional] = useState<any[]>([])
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    apiFetch('/reliability/leaderboard?min_obs=20').then((r) => {
      if (r.ok) {
        setRanked(r.json.data?.ranked ?? [])
        setProvisional(r.json.data?.provisional ?? [])
      }
      setLoaded(true)
    })
  }, [])

  // biome-ignore lint/suspicious/noExplicitAny: API row shape
  const row = (c: any, dim: boolean) => (
    <div
      key={c.carrier_code}
      className={`flex items-center justify-between gap-4 ${dim ? 'opacity-60' : ''}`}
    >
      <span className="font-mono text-xs w-8 text-right text-[var(--text-muted)]">
        {c.rank != null ? `#${c.rank}` : ''}
      </span>
      <span className="font-mono text-sm w-20">{c.carrier_code}</span>
      <ReliabilityBar pct={Math.round(c.on_time_pct ?? 0)} />
      <VolatilityChip v={c.volatility} />
      <span className="text-xs text-[var(--text-muted)] w-24 text-right">{c.observations} obs</span>
    </div>
  )

  return (
    <section className="bg-surface-0 border border-border rounded-2xl p-6">
      <h2 className="font-display font-bold text-lg mb-1">Carrier league table</h2>
      <p className="text-xs text-[var(--text-muted)] mb-4">
        Carriers with 20+ observations, ranked by overall on-time reliability.
      </p>
      {!loaded ? (
        <p className="text-sm text-[var(--text-muted)]">Loading…</p>
      ) : ranked.length === 0 && provisional.length === 0 ? (
        <p className="text-sm text-[var(--text-muted)]">
          No reliability data yet. The league fills in as more shipments are tracked across lanes.
        </p>
      ) : (
        <div className="space-y-3">
          {ranked.map((c) => row(c, false))}
          {provisional.length > 0 && (
            <>
              <p className="text-[10px] font-mono uppercase tracking-wide text-[var(--text-muted)] pt-2">
                Too little data to rank (under 20 observations)
              </p>
              {provisional.map((c) => row(c, true))}
            </>
          )}
        </div>
      )}
    </section>
  )
}

interface AlertRow {
  id: string
  alert_type: 'blank' | 'cutoff' | 'slip'
  origin_unlocode: string | null
  destination_unlocode: string | null
  detail: {
    headline?: string
    message?: string
    stat?: { label: string; value: string } | null
  } | null
  created_at: string
}
const ALERT_META: Record<string, { label: string; cls: string }> = {
  cutoff: { label: 'Cut-off soon', cls: 'text-danger bg-danger/10' },
  blank: { label: 'Blank sailing', cls: 'text-warn bg-warn/10' },
  slip: { label: 'Schedule slip', cls: 'text-warn bg-warn/10' },
  reliability: { label: 'Reliability drop', cls: 'text-danger bg-danger/10' },
}
function timeAgo(iso: string) {
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000)
  if (m < 60) return `${Math.max(m, 0)}m ago`
  const h = Math.round(m / 60)
  return h < 24 ? `${h}h ago` : `${Math.round(h / 24)}d ago`
}

// Review-and-approve inbox: detected schedule events wait here; nothing is emailed until the user
// approves. This is the anti-spam contract made visible.
function AlertInbox() {
  const [alerts, setAlerts] = useState<AlertRow[]>([])
  const [loaded, setLoaded] = useState(false)
  const [busy, setBusy] = useState(false)

  async function load() {
    const r = await apiFetch('/alerts?status=pending')
    if (r.ok) setAlerts(r.json.data ?? [])
    setLoaded(true)
  }
  // biome-ignore lint/correctness/useExhaustiveDependencies: load is stable; run once on mount
  useEffect(() => {
    load()
  }, [])

  async function act(id: string, action: 'approve' | 'dismiss') {
    setBusy(true)
    setAlerts((prev) => prev.filter((a) => a.id !== id)) // optimistic
    await apiFetch(`/alerts/${id}/${action}`, { method: 'POST' })
    setBusy(false)
  }
  async function bulk(action: 'approve-all' | 'dismiss-all') {
    setBusy(true)
    await apiFetch(`/alerts/${action}`, { method: 'POST' })
    await load()
    setBusy(false)
  }

  return (
    <section className="bg-surface-0 border border-border rounded-2xl p-6">
      <div className="flex items-center gap-3 mb-1">
        <h2 className="font-display font-bold text-lg">Alerts</h2>
        {alerts.length > 0 && (
          <span className="text-[11px] font-mono bg-warn/15 text-warn rounded-full px-2 py-0.5">
            {alerts.length} pending review
          </span>
        )}
      </div>
      <p className="text-xs text-[var(--text-muted)] mb-4">
        Detection runs hourly. Nothing is emailed until you approve it. Send the events that matter,
        dismiss the rest.
      </p>

      {!loaded ? (
        <p className="text-sm text-[var(--text-muted)]">Loading…</p>
      ) : alerts.length === 0 ? (
        <p className="text-sm text-[var(--text-muted)]">
          No alerts to review. New blank sailings, closing cut-offs, and schedule slips on your
          watched lanes will appear here for approval.
        </p>
      ) : (
        <>
          <div className="space-y-2">
            {alerts.map((a) => {
              const meta = ALERT_META[a.alert_type] ?? {
                label: a.alert_type,
                cls: 'text-warn bg-warn/10',
              }
              const lane = `${a.origin_unlocode ?? '?'}→${a.destination_unlocode ?? '?'}`
              return (
                <div
                  key={a.id}
                  className="flex items-start justify-between gap-3 bg-surface-1 border border-border rounded-xl px-4 py-3"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <span
                        className={`text-[10px] font-mono rounded-full px-2 py-0.5 ${meta.cls}`}
                      >
                        {meta.label}
                      </span>
                      <span className="font-mono text-xs text-[var(--text-secondary)]">{lane}</span>
                      <span className="text-[10px] text-[var(--text-muted)]">
                        · {timeAgo(a.created_at)}
                      </span>
                    </div>
                    <div className="text-sm text-[var(--text-primary)] truncate">
                      {a.detail?.headline ?? 'Schedule event'}
                    </div>
                    {a.detail?.stat && (
                      <div className="text-xs text-[var(--text-secondary)] mt-0.5">
                        <span className="font-medium">{a.detail.stat.value}</span>{' '}
                        {a.detail.stat.label}
                      </div>
                    )}
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => act(a.id, 'approve')}
                      className="rounded-lg bg-signal/15 text-signal text-xs font-medium px-3 py-1.5 hover:bg-signal/25 disabled:opacity-50"
                    >
                      Send
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => act(a.id, 'dismiss')}
                      className="rounded-lg border border-border text-xs text-[var(--text-muted)] px-3 py-1.5 hover:text-[var(--text-primary)] disabled:opacity-50"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
          <div className="flex items-center justify-between gap-3 mt-4 pt-3 border-t border-border/50">
            <span className="text-[11px] text-[var(--text-muted)]">
              Only events you approve are emailed.
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={busy}
                onClick={() => bulk('approve-all')}
                className="text-xs text-signal hover:underline disabled:opacity-50"
              >
                Approve all
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => bulk('dismiss-all')}
                className="text-xs text-[var(--text-muted)] hover:text-[var(--text-primary)] disabled:opacity-50"
              >
                Dismiss all
              </button>
            </div>
          </div>
        </>
      )}
    </section>
  )
}

interface Watch {
  id: string
  origin_unlocode: string
  destination_unlocode: string
  carrier_code: string | null
  drop_threshold_pts: number
}

function WatchLanes() {
  const [watches, setWatches] = useState<Watch[]>([])
  const [origin, setOrigin] = useState('')
  const [destination, setDestination] = useState('')
  const [carrier, setCarrier] = useState('')

  async function load() {
    const r = await apiFetch('/watches')
    if (r.ok) setWatches(r.json.data ?? [])
  }
  // biome-ignore lint/correctness/useExhaustiveDependencies: load is stable; run once on mount
  useEffect(() => {
    load()
  }, [])

  async function add() {
    if (!origin || !destination) return
    await apiFetch('/watches', {
      method: 'POST',
      body: JSON.stringify({ origin, destination, carrier_code: carrier || undefined }),
    })
    setOrigin('')
    setDestination('')
    setCarrier('')
    load()
  }
  async function remove(id: string) {
    await apiFetch(`/watches/${id}`, { method: 'DELETE' })
    load()
  }

  return (
    <section className="bg-surface-0 border border-border rounded-2xl p-6">
      <h2 className="font-display font-bold text-lg mb-1">Watched lanes</h2>
      <p className="text-xs text-[var(--text-muted)] mb-4">
        Watch a lane and we detect blank sailings, closing cut-offs, schedule slips and reliability
        drops. They queue in Alerts above for your approval (no auto-email).
      </p>
      <div className="flex flex-wrap items-end gap-2 mb-4">
        <input
          value={origin}
          onChange={(e) => setOrigin(e.target.value.toUpperCase())}
          placeholder="Origin"
          className="bg-surface-1 border border-border rounded-lg px-3 py-2 font-mono w-24 text-[var(--text-primary)] focus:border-signal outline-none"
        />
        <input
          value={destination}
          onChange={(e) => setDestination(e.target.value.toUpperCase())}
          placeholder="Dest"
          className="bg-surface-1 border border-border rounded-lg px-3 py-2 font-mono w-24 text-[var(--text-primary)] focus:border-signal outline-none"
        />
        <input
          value={carrier}
          onChange={(e) => setCarrier(e.target.value.toUpperCase())}
          placeholder="Carrier (opt)"
          className="bg-surface-1 border border-border rounded-lg px-3 py-2 font-mono w-32 text-[var(--text-primary)] focus:border-signal outline-none"
        />
        <button
          type="button"
          onClick={add}
          className="bg-signal text-on-accent font-bold px-4 py-2 rounded-lg"
        >
          Watch
        </button>
      </div>
      {watches.length === 0 ? (
        <p className="text-sm text-[var(--text-muted)]">No lanes watched yet.</p>
      ) : (
        <div className="space-y-2">
          {watches.map((w) => (
            <div
              key={w.id}
              className="flex items-center justify-between text-sm border-b border-border/50 pb-2 last:border-0"
            >
              <span className="font-mono">
                {w.origin_unlocode}→{w.destination_unlocode}
                {w.carrier_code ? ` · ${w.carrier_code}` : ''}
              </span>
              <button
                type="button"
                onClick={() => remove(w.id)}
                className="text-xs text-[var(--text-muted)] hover:text-danger"
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}
